import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Inbox, Clock } from "lucide-react";

const STATUS_STYLES = {
  sent: "bg-emerald-100 text-emerald-800",
  approved: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-800",
};

/**
 * Shared inbox / compose for students, teachers and managers.
 * Teacher -> student messages are held for manager approval by the backend.
 */
export function MessagesTab({ role }) {
  const [box, setBox] = useState("inbox");
  const [list, setList] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ recipient_ids: [], subject: "", body: "" });

  const load = (b = box) => api.get(`/messages?box=${b}`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(box); }, [box]); // eslint-disable-line
  useEffect(() => { api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([])); }, []);

  const cname = (id) => contacts.find((c) => c.id === id)?.name || "—";

  async function send() {
    if (form.recipient_ids.length === 0) { toast.error("Choose at least one recipient"); return; }
    if (!form.subject || !form.body) { toast.error("Subject and message are required"); return; }
    try {
      const r = await api.post("/messages", form);
      toast.success(r.data.status === "pending"
        ? "Message sent for manager approval"
        : "Message delivered");
      setOpen(false); setForm({ recipient_ids: [], subject: "", body: "" });
      load(box);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const boxes = role === "manager"
    ? [["inbox", "Inbox"], ["sent", "Sent"], ["pending", "Awaiting approval"], ["all", "All"]]
    : [["inbox", "Inbox"], ["sent", "Sent"]];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {boxes.map(([k, l]) => (
            <Button key={k} data-testid={`msg-box-${k}`} variant={box === k ? "default" : "outline"}
              className={`rounded-full h-9 ${box === k ? "bg-[#0F1E4F] text-white" : ""}`} onClick={() => setBox(k)}>
              {k === "inbox" ? <Inbox className="h-4 w-4 mr-1.5" /> : k === "pending" ? <Clock className="h-4 w-4 mr-1.5" /> : null}
              {l}
            </Button>
          ))}
        </div>
        <Button data-testid="msg-compose" className="rounded-full bg-[#0F1E4F] text-white" onClick={() => { setForm({ recipient_ids: [], subject: "", body: "" }); setOpen(true); }}>
          <Send className="h-4 w-4 mr-1.5" /> New message
        </Button>
      </div>

      {role === "teacher" && (
        <p className="text-xs text-slate-500">
          Messages you send to students are reviewed by a manager before delivery.
        </p>
      )}

      <div className="space-y-3">
        {list.length === 0 && (
          <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center">
            No messages in this folder.
          </Card>
        )}
        {list.map((m) => (
          <Card key={m.id} data-testid={`msg-${m.id}`} className="p-5 rounded-2xl border-slate-200">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-[#B8860B]">
                  {box === "sent" ? `To ${(m.recipient_ids || []).map(cname).join(", ")}` : `From ${m.author_name}`}
                  {" · "}{new Date(m.created_at).toLocaleString()}
                </div>
                <div className="mt-1 font-serif font-black text-[#0F1E4F]">{m.subject}</div>
                <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{m.body}</p>
                {m.status === "rejected" && m.reject_reason && (
                  <p className="mt-2 text-xs text-red-700">Manager note: {m.reject_reason}</p>
                )}
              </div>
              <Badge className={`rounded-full border-0 capitalize shrink-0 ${STATUS_STYLES[m.status] || "bg-slate-100 text-slate-700"}`}>
                {m.status === "approved" ? "delivered" : m.status}
              </Badge>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>New message</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Recipients</Label>
              <div className="mt-1 max-h-44 overflow-auto border border-slate-200 rounded-xl p-2 space-y-1">
                {contacts.length === 0 && <div className="text-sm text-slate-500 p-2">No contacts available yet.</div>}
                {contacts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1 hover:bg-slate-50">
                    <input type="checkbox" data-testid={`msg-to-${c.id}`} checked={form.recipient_ids.includes(c.id)}
                      onChange={(e) => setForm({ ...form, recipient_ids: e.target.checked ? [...form.recipient_ids, c.id] : form.recipient_ids.filter((x) => x !== c.id) })} />
                    <span>{c.name} <span className="text-slate-400">({c.role})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <Input data-testid="msg-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-11 rounded-xl" />
            <Textarea data-testid="msg-body" rows={6} placeholder="Write your message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="msg-send" className="rounded-full bg-[#0F1E4F] text-white" onClick={send}>Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
